import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';

// Layout Component (We will extract this later)
import Layout from './components/Layout';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';

// Pages (We will create these next)
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import LeadList from './pages/leads/LeadList';
import CustomerList from './pages/customers/CustomerList';
import UserManagement from './pages/users/UserManagement';
import Followups from './pages/followups/Followups';
import Reports from './pages/reports/Reports';
import Settings from './pages/settings/Settings';
import MasterTable from './pages/settings/MasterTable';
import UserCalendar from './pages/users/UserCalendar';
import Profile from './pages/Profile';
import SendEmail from './pages/email/SendEmail';
import RolesList from './pages/roles/RolesList';
import DeliveryList from './pages/deliveries/DeliveryList';
import AttendanceList from './pages/attendance/AttendanceList';
const ProtectedRoute = ({ feature, roles, permission, children }) => {
  const { user, hasFeature, hasPermission } = useAuth();
  
  if (user?.role === 'Admin') return children;
  
  if (roles && roles.includes(user?.role)) return children;
  if (feature && hasFeature(feature)) return children;
  if (permission && hasPermission(permission)) return children;
  
  return <Navigate to={user?.role === 'Delivery Boy' ? "/deliveries" : "/dashboard"} replace />;
};

const AppRoutes = () => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) return null;

  const defaultHome = user?.role === 'Delivery Boy' ? "/deliveries" : "/dashboard";

  return (
    <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* Protected Routes inside Layout */}
        <Route path="/" element={isAuthenticated ? <Layout /> : <Navigate to="/login" />}>
          <Route index element={<Navigate to={defaultHome} replace />} />
          <Route path="dashboard" element={user?.role === 'Delivery Boy' ? <Navigate to="/deliveries" replace /> : <Dashboard />} />
          <Route path="profile" element={<Profile />} />
          
          {/* Leads */}
          <Route path="leads" element={<ProtectedRoute feature="leads"><LeadList /></ProtectedRoute>} />
          
          {/* Customers */}
          <Route path="customers" element={<ProtectedRoute feature="leads"><CustomerList /></ProtectedRoute>} />
          {/* Deliveries */}
          <Route path="deliveries" element={<ProtectedRoute permission="deliveries:read"><DeliveryList /></ProtectedRoute>} />
          
          {/* Followups */}
          <Route path="followups" element={<ProtectedRoute feature="followups"><Followups /></ProtectedRoute>} />
          
          {/* Email */}
          <Route path="send-email" element={<ProtectedRoute roles={['Admin']}><SendEmail /></ProtectedRoute>} />
          
          {/* Reports */}
          <Route path="reports" element={<ProtectedRoute feature="reports"><Reports /></ProtectedRoute>} />
          <Route path="attendance" element={<ProtectedRoute roles={['Admin']}><AttendanceList /></ProtectedRoute>} />          
          {/* Admin/Settings routes */}
          <Route path="users" element={<ProtectedRoute permission="users:read"><UserManagement /></ProtectedRoute>} />
          <Route path="roles" element={<ProtectedRoute permission="roles:read"><RolesList /></ProtectedRoute>} />
          <Route path="user-calendar" element={<ProtectedRoute feature="followups"><UserCalendar /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute permission="settings:read"><Settings /></ProtectedRoute>} />
          <Route path="masters/status" element={<ProtectedRoute feature="masters"><MasterTable key="status" title="Manage Statuses" /></ProtectedRoute>} />
          <Route path="masters/delivery-statuses" element={<ProtectedRoute feature="masters"><MasterTable key="delivery-statuses" title="Manage Delivery Statuses" /></ProtectedRoute>} />
          <Route path="masters/machines" element={<ProtectedRoute feature="masters"><MasterTable key="machines" title="Manage Machines" /></ProtectedRoute>} />
          <Route path="masters/email-templates" element={<ProtectedRoute feature="masters"><MasterTable key="email-templates" title="Manage Email Templates" mockItems={[
            { id: 1, name: 'Welcome Email', subject: 'Welcome to our agency!', status: 'Active', createdAt: '2026-07-22' },
            { id: 2, name: 'Follow-up Reminder', subject: 'Just checking in on your printing needs', status: 'Active', createdAt: '2026-07-21' }
          ]} /></ProtectedRoute>} />
          <Route path="masters/lead-sources" element={<ProtectedRoute feature="masters"><MasterTable key="lead-sources" title="Manage Lead Sources" /></ProtectedRoute>} />
          <Route path="masters/customer-types" element={<ProtectedRoute feature="masters"><MasterTable key="customer-types" title="Manage Customer Types" /></ProtectedRoute>} />
        </Route>
      </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Toaster position="top-right" />
        <AppRoutes />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
